import React, { useState } from 'react';
import AppHeader from '../../../components/AppHeader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faCog, faCreditCard, faGlobe, faPalette, faSave, faShieldAlt, faStore, faToggleOff, faToggleOn } from '@fortawesome/free-solid-svg-icons';

const SettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({
    general: {
      salonName: 'Premium Salon',
      address: 'Václavské náměstí 1, Praha 1',
      phone: '+420 123 456 789',
      email: 'info@premiumsalon.cz',
      website: 'www.premiumsalon.cz',
      timezone: 'Europe/Prague',
      currency: 'CZK',
      language: 'cs',
    },
    notifications: {
      emailNotifications: true,
      smsNotifications: false,
      pushNotifications: true,
      reminderEmails: true,
      marketingEmails: false,
    },
    appearance: {
      theme: 'light',
      primaryColor: 'green',
      sidebarCollapsed: false,
      compactMode: false,
    },
    business: {
      workingHours: {
        monday: { start: '08:00', end: '18:00', closed: false },
        tuesday: { start: '08:00', end: '18:00', closed: false },
        wednesday: { start: '08:00', end: '18:00', closed: false },
        thursday: { start: '08:00', end: '18:00', closed: false },
        friday: { start: '08:00', end: '18:00', closed: false },
        saturday: { start: '09:00', end: '16:00', closed: false },
        sunday: { start: '09:00', end: '16:00', closed: true },
      },
      bookingSettings: {
        advanceBookingDays: 30,
        cancellationHours: 24,
        confirmationRequired: true,
        autoConfirm: false,
      },
    },
  });

  const breadcrumb = [{ label: 'Nastavení' }, { label: 'Obecné nastavení' }];

  const tabs = [
    { id: 'general', name: 'Obecné', icon: faCog },
    { id: 'notifications', name: 'Oznámení', icon: faBell },
    { id: 'appearance', name: 'Vzhled', icon: faPalette },
    { id: 'business', name: 'Obchodní', icon: faStore },
    { id: 'security', name: 'Bezpečnost', icon: faShieldAlt },
    { id: 'integrations', name: 'Integrace', icon: faGlobe },
    { id: 'billing', name: 'Fakturace', icon: faCreditCard },
  ];

  const handleSettingChange = (category: string, key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category as keyof typeof prev],
        [key]: value,
      },
    }));
  };

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Název salonu</label>
          <input
            type="text"
            value={settings.general.salonName}
            onChange={e => handleSettingChange('general', 'salonName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Telefon</label>
          <input
            type="tel"
            value={settings.general.phone}
            onChange={e => handleSettingChange('general', 'phone', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">E-mail</label>
          <input
            type="email"
            value={settings.general.email}
            onChange={e => handleSettingChange('general', 'email', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Webová stránka</label>
          <input
            type="url"
            value={settings.general.website}
            onChange={e => handleSettingChange('general', 'website', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Adresa</label>
        <textarea
          rows={3}
          value={settings.general.address}
          onChange={e => handleSettingChange('general', 'address', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Časové pásmo</label>
          <select
            value={settings.general.timezone}
            onChange={e => handleSettingChange('general', 'timezone', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
          >
            <option value="Europe/Prague">Evropa/Praha</option>
            <option value="Europe/Bratislava">Evropa/Bratislava</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Měna</label>
          <select
            value={settings.general.currency}
            onChange={e => handleSettingChange('general', 'currency', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
          >
            <option value="CZK">CZK - Česká koruna</option>
            <option value="EUR">EUR - Euro</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Jazyk</label>
          <select
            value={settings.general.language}
            onChange={e => handleSettingChange('general', 'language', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
          >
            <option value="cs">Čeština</option>
            <option value="en">English</option>
            <option value="de">Deutsch</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderNotificationsSettings = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
          <div>
            <h3 className="font-medium text-gray-900">E-mailová oznámení</h3>
            <p className="text-sm text-gray-600">Dostávat oznámení na e-mail</p>
          </div>
          <button
            onClick={() => handleSettingChange('notifications', 'emailNotifications', !settings.notifications.emailNotifications)}
            className={`${settings.notifications.emailNotifications ? 'text-green-600' : 'text-gray-400'}`}
          >
            <FontAwesomeIcon icon={settings.notifications.emailNotifications ? faToggleOn : faToggleOff} className="w-6 h-6" />
          </button>
        </div>

        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
          <div>
            <h3 className="font-medium text-gray-900">SMS oznámení</h3>
            <p className="text-sm text-gray-600">Dostávat oznámení na telefon</p>
          </div>
          <button
            onClick={() => handleSettingChange('notifications', 'smsNotifications', !settings.notifications.smsNotifications)}
            className={`${settings.notifications.smsNotifications ? 'text-green-600' : 'text-gray-400'}`}
          >
            <FontAwesomeIcon icon={settings.notifications.smsNotifications ? faToggleOn : faToggleOff} className="w-6 h-6" />
          </button>
        </div>

        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
          <div>
            <h3 className="font-medium text-gray-900">Push oznámení</h3>
            <p className="text-sm text-gray-600">Oznámení v aplikaci</p>
          </div>
          <button
            onClick={() => handleSettingChange('notifications', 'pushNotifications', !settings.notifications.pushNotifications)}
            className={`${settings.notifications.pushNotifications ? 'text-green-600' : 'text-gray-400'}`}
          >
            <FontAwesomeIcon icon={settings.notifications.pushNotifications ? faToggleOn : faToggleOff} className="w-6 h-6" />
          </button>
        </div>

        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
          <div>
            <h3 className="font-medium text-gray-900">Připomínky</h3>
            <p className="text-sm text-gray-600">Automatické připomínky rezervací</p>
          </div>
          <button
            onClick={() => handleSettingChange('notifications', 'reminderEmails', !settings.notifications.reminderEmails)}
            className={`${settings.notifications.reminderEmails ? 'text-green-600' : 'text-gray-400'}`}
          >
            <FontAwesomeIcon icon={settings.notifications.reminderEmails ? faToggleOn : faToggleOff} className="w-6 h-6" />
          </button>
        </div>

        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
          <div>
            <h3 className="font-medium text-gray-900">Marketingové e-maily</h3>
            <p className="text-sm text-gray-600">Dostávat marketingové informace</p>
          </div>
          <button
            onClick={() => handleSettingChange('notifications', 'marketingEmails', !settings.notifications.marketingEmails)}
            className={`${settings.notifications.marketingEmails ? 'text-green-600' : 'text-gray-400'}`}
          >
            <FontAwesomeIcon icon={settings.notifications.marketingEmails ? faToggleOn : faToggleOff} className="w-6 h-6" />
          </button>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return renderGeneralSettings();
      case 'notifications':
        return renderNotificationsSettings();
      default:
        return (
          <div className="text-center py-12">
            <FontAwesomeIcon icon={faCog} className="w-16 h-16 text-gray-400 mb-4" />
            <p className="text-gray-500">Tato sekce bude implementována</p>
          </div>
        );
    }
  };

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Obecné nastavení" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Settings Sidebar */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-lg shadow-sm">
                  <div className="p-6">
                    <nav className="space-y-1">
                      {tabs.map(tab => (
                        <button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                            activeTab === tab.id ? 'bg-green-50 text-green-700 border border-green-200' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                          }`}
                        >
                          <FontAwesomeIcon icon={tab.icon} className="w-4 h-4 mr-3" />
                          {tab.name}
                        </button>
                      ))}
                    </nav>
                  </div>
                </div>
              </div>

              {/* Settings Content */}
              <div className="lg:col-span-3">
                <div className="bg-white rounded-lg shadow-sm">
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h2 className="text-xl font-semibold text-gray-900">{tabs.find(tab => tab.id === activeTab)?.name}</h2>
                      <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                        <FontAwesomeIcon icon={faSave} className="mr-2" />
                        Uložit změny
                      </button>
                    </div>
                  </div>
                  <div className="p-6">{renderTabContent()}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
